import React from 'react'
import type { ReactNode } from 'react'

import useProductSearch from '@hooks/shared/products/useProductSearch'
import { Select, Spin } from 'antd'
import { useTranslation } from 'react-i18next'

/**
 * 商品搜尋 Select 組件
 *
 * @param {Object} productService - Product Service (admin 或 operator)
 * @param {Function} onChange - 選擇商品時的回調
 *   - 預設模式: (productId) => void
 *   - labelInValue 模式: (option) => void, option = { value, label, sku, name, price, ... }
 * @param {number|Object} value - 當前選中的值
 *   - 預設模式: 商品 ID
 *   - labelInValue 模式: { value, label }
 * @param {string} placeholder - Placeholder 文字
 * @param {Object} searchOptions - useProductSearch 的配置選項
 * @param {Function} onSelect - 選擇時的額外回調 (productId, fullOption) => void
 * @param {string} merchantId - 篩選指定 merchant 的商品
 * @param {Array} excludeIds - 排除指定的商品 ID
 * @param {Object} ...selectProps - 其他 Ant Design Select 的 props
 */
interface ProductOption {
  value: string;
  label: string;
  sku?: string;
  name?: string;
  price?: number;
  [key: string]: unknown;
}

interface ProductSelectProps {
  // productService 是 .js/.jsx service 物件，runtime shape 多變（admin/operator 兩套），用 unknown 放行
  productService?: unknown;
  // onChange 在預設模式回 productId，labelInValue 模式回 option 物件；用 any 放行避免型別膨脹
  onChange?: (valueOrOption: any, option?: any) => void;
  // value 在預設模式為 productId，labelInValue 模式為 { value, label }
  value?: any;
  placeholder?: string;
  searchOptions?: Record<string, unknown>;
  onSelect?: (valueOrOption: any, fullOption: ProductOption | undefined) => void;
  merchantId?: string | null;
  excludeIds?: string[];
  defaultOptions?: ProductOption[];
  // 透傳到底層 antd Select 的其他 props
  [key: string]: unknown;
}

const ProductSelect: React.FC<ProductSelectProps> = ({
  productService,
  onChange,
  value,
  placeholder,
  searchOptions = {},
  onSelect,
  merchantId,
  excludeIds = [],
  defaultOptions = [],
  ...selectProps
}) => {
  const { t } = useTranslation()
  // useProductSearch 是 .js + JSDoc 把 productService 標 Object / return 標 Object，
  // 在 TS strict 下不接受 unknown / 缺乏欄位推導；此 .js 邊界 cast 為 any 放行。
  const useProductSearchAny = useProductSearch as unknown as (
    service: unknown,
    options: Record<string, unknown>
  ) => any;
  const {
    products,
    loading,
    searchKeyword,
    inputValue,
    minSearchLength,
    searchProducts,
    clearProducts
  } = useProductSearchAny(
    productService,
    { ...searchOptions, merchantId, excludeIds }
  )

  const HighlightText = ({ text, highlight }: { text: string; highlight: string }): ReactNode => {
    if (!highlight || !highlight.trim()) {
      return <span>{text}</span>;
    }
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);
    return (
      <span>
        {parts.map((part, i) => (
          regex.test(part) ? (
            <span key={i} style={{ backgroundColor: '#fff566', padding: '0 2px', borderRadius: '2px' }}>
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        ))}
      </span>
    );
  };

  const handleChange = (valueOrOption: any, option: any) => {
    onChange?.(valueOrOption, option)
  }

  const handleSelect = (valueOrOption: any, option: any) => {
    // 支持額外的 onSelect 回調
    if (onSelect) {
      // 找到完整的 product option
      const fullOption = (products as ProductOption[]).find(
        (p) => p.value === (option?.value || valueOrOption)
      )
      onSelect(valueOrOption, fullOption)
    }
  }

  const handleClear = () => {
    clearProducts()
    onChange?.(null)
  }

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder || t('common.select.search_product_placeholder')}
      value={value}
      loading={loading}
      options={products.length > 0 ? products : defaultOptions}
      onSearch={searchProducts}
      onChange={handleChange}
      onSelect={handleSelect}
      onClear={handleClear}
      filterOption={false} // 使用伺服器端搜尋
      notFoundContent={
        loading ? (
          <div style={{ textAlign: 'center', padding: '8px' }}>
            <Spin size="small" />
          </div>
        ) : (!inputValue || inputValue.length < minSearchLength) ? (
          <div style={{ padding: '8px', color: '#999', textAlign: 'center' }}>
            {t('common.select.min_length_hint', { count: minSearchLength })}
          </div>
        ) : (
          <div style={{ padding: '8px', color: '#999', textAlign: 'center' }}>
            {t('common.select.no_results')}
          </div>
        )
      }
      optionRender={(option) => (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
          <div style={{ fontWeight: 'bold', fontSize: '14px', lineHeight: '1.4' }}>
            <HighlightText text={option.data.sku} highlight={searchKeyword} />
          </div>
          <div style={{ fontSize: '12px', color: '#8c8c8c', lineHeight: '1.4' }}>
            <HighlightText text={option.data.name} highlight={searchKeyword} />
          </div>
        </div>
      )}
      {...selectProps}
    />
  )
}

export default ProductSelect
